import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { PreviewDataType } from '../../shared/enums/preview-data-type.enum';
import { DialogService } from 'primeng/dynamicdialog';
import { TableRequestData } from '../../shared/models/table/table-request-param';
import { catchError, EMPTY, filter, finalize, Observable, tap } from 'rxjs';
import { Dokument } from '../../shared/models/dokument';
import { InboxType } from '../../shared/enums/inbox-type.enum';
import { TableBaseAbstractComponent } from '../../shared/components/table-base-abstract.component';
import { Button } from 'primeng/button';
import { DatePipe } from '@angular/common';
import { Card } from 'primeng/card';
import { FormsModule } from '@angular/forms';
import { Checkbox } from 'primeng/checkbox';
import { PreviewDataWrapperComponent } from '../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { ApplicationDetailsComponent } from './components/application-details/application-details.component';
import { TableResponse } from '../../shared/models/table-response';
import { InputText } from 'primeng/inputtext';
import { ControlErrorDirective } from '../../shared/directive/control-error.directive';
import { Select } from 'primeng/select';
import {
  ApplicationCode,
  getApplicationCodeDropdownValueByContext,
} from '../../shared/enums/application_code';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Paginator } from 'primeng/paginator';
import { SpinnerService } from '../../core/services/spinner.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { HttpUtils } from '../../shared/utils/http.utils';
import { ExportType } from '../../shared/enums/export-type.enum';
import { InboxApi } from '../../shared/services/api/inbox.api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExportApi } from '../../shared/services/api/export.api';
import { TableRowEvent } from '../../shared/models/custom-types';
import { DokumentApi } from '../../shared/services/api/dokument.api';
import { ShouldShowCorrespondenceIconPipe } from '../../shared/pipes/should-show-correspondence-icon.pipe';
import { InfoDialog } from '../../shared/components/info-dialog/info-dialog';
import {
  RedirectMessage,
  RedirectMessageContents,
} from '../../shared/enums/redirect-message.enum';
import { ApplicationContextService } from '../../core/services/application-context.service';
import { IsAdresatZusPipe } from './is-adresat-zus-pipe';
import { HttpResponseService } from '../../core/services/http-error-response.service';
import { Popover } from 'primeng/popover';
import { ApplicationService } from '../../shared/services/application.service';
import { ZS_6_APPLICATION } from '../application-cards/application-filters-definition';

@Component({
  selector: 'app-application-sent',
  imports: [
    TableModule,
    Button,
    Card,
    DatePipe,
    FormsModule,
    TooltipModule,
    Checkbox,
    PreviewDataWrapperComponent,
    InputText,
    ControlErrorDirective,
    Select,
    DatePicker,
    ProgressSpinner,
    Paginator,
    ShouldShowCorrespondenceIconPipe,
    IsAdresatZusPipe,
    Popover,
  ],
  templateUrl: './application-sent.component.html',
  styleUrl: './application-sent.component.scss',
  providers: [InboxApi, ExportApi, DokumentApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ApplicationSentComponent
  extends TableBaseAbstractComponent<Dokument>
  implements OnInit
{
  @ViewChild('table') override tableRef!: Table;
  @ViewChild('pop') popRef!: Popover;
  public redirectMessage = input<RedirectMessage>();
  protected inboxType = signal<InboxType>(InboxType.TO_RECEIVE);
  protected readonly InboxType = InboxType;
  protected readonly PreviewDataType = PreviewDataType;
  protected readonly ApplicationCode = ApplicationCode;
  protected selectedDocuments: Dokument[] = [];
  readonly #exportApi = inject(ExportApi);
  readonly #inboxApi = inject(InboxApi);
  readonly #dialogService = inject(DialogService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #applicationContextService = inject(ApplicationContextService);
  readonly #applicationService = inject(ApplicationService);
  #isCreating = signal<boolean>(false);
  #shouldRunInboxTypeChangEffect: boolean = false;
  protected tableFilters = {
    documentType: null,
    sender: null,
    documentNumber: null,
    sentDate: null,
  };
  protected dropdownValue = computed(() =>
    getApplicationCodeDropdownValueByContext(
      this.#applicationContextService.isOrganizationContext(),
    ),
  );

  ngOnInit(): void {
    this.#handleRedirectMessageIfExist();
  }

  constructor() {
    super();
    this.#initInboxTypeChangeEffect();
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<Dokument>> => {
    params.sortBy = params?.sortBy ?? 'dataWplywu';
    return this.#inboxApi.getAllSentApplicationDocuments(this.tableRequest());
  };

  protected isRowSelectable = (e: TableRowEvent) => {
    return e.data.adresat !== 'ZUS';
  };

  protected openShowApplicationModal(application: Dokument): void {
    const ref = this.#dialogService.open(ApplicationDetailsComponent, {
      closable: true,
      header: `Szczegóły wniosku`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '60vw',
      inputValues: {
        ['application']: application,
        ['applicationId']: application.id,
        ['inboxType']: this.inboxType(),
      },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });

    if (!ref) return;

    ref.onClose
      .pipe(
        filter(Boolean),
        tap((result) => {
          if (result === 'switch-to-sent') {
            this.inboxType.set(InboxType.TO_RECEIVE);
          }

          if (result === 'switch-to-archive') {
            this.inboxType.set(InboxType.ARCHIVED);
          }

          this.reloadData();
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected archiveDocumentDialog(): void {
    if (!this.selectedDocuments.length) {
      return;
    }

    const header =
      this.selectedDocuments.length === 1
        ? 'Chcesz przenieść wybrany dokument do archiwum? Dokument będzie dostępny po wybraniu filtra „Zarchiwizowane”.'
        : 'Chcesz przenieść wybrane dokumenty do archiwum? Dokumenty będą dostępne po wybraniu filtra „Zarchiwizowane”.';

    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Ok',
      },
    });
    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => {
            this.#archiveDocument();
            this.inboxType.set(InboxType.ARCHIVED);
            this.reloadData();
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected createNewDokument(dokument: Dokument): void {
    if (this.#isCreating()) {
      return;
    }

    this.#isCreating.set(true);
    this.#applicationService
      .initApplicationFlowWithIdKorelacji(ZS_6_APPLICATION, dokument.id)
      .pipe(
        finalize(() => {
          this.#isCreating.set(false);
        }),
      )
      .subscribe();
  }

  protected restoreFromArchiveDocumentDialog(): void {
    if (!this.selectedDocuments.length) {
      return;
    }

    const header =
      this.selectedDocuments.length === 1
        ? 'Chcesz przywrócić wybrany dokument z archiwum?'
        : 'Chcesz przywrócić wybrane dokumenty z archiwum?';

    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Ok',
      },
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#restoreFromArchiveDocument()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected exportSelected(): void {
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(
        new Set(this.selectedDocuments.map((value) => value.id)),
        ExportType.APPLICATION,
      )
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected toggleInboxType(type: InboxType): void {
    const current = this.inboxType();
    if (current === type) {
      this.inboxType.set(InboxType.ALL);
      return;
    }
    this.inboxType.set(type);
  }

  #archiveDocument(): void {
    this.#spinnerService.show();
    this.#inboxApi
      .archiveDocuments$(
        new Set(this.selectedDocuments.map((value) => value.id)),
      )
      .pipe(
        tap(() => {
          const summary =
            this.selectedDocuments.length === 1
              ? 'Poprawnie przeniesiono dokument do archiwum'
              : `Poprawnie przeniesiono dokumenty do archiwum`;

          this.#httpResponseService.showSuccessMessage(summary);
        }),
        catchError((err) => {
          const summary =
            this.selectedDocuments.length === 1
              ? 'Wystąpił błąd podczas próby przeniesienia dokumentu do archiwum'
              : 'Wystąpił błąd podczas próby przeniesienia dokumentów do archiwum';
          this.#httpResponseService.showErrorMessage(err, summary);
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.selectedDocuments = [];
          this.#spinnerService.hide();
          this.reloadData();
        }),
      )
      .subscribe();
  }

  #restoreFromArchiveDocument(): void {
    this.#spinnerService.show();
    this.#inboxApi
      .restoreFromArchiveDocuments$(
        new Set(this.selectedDocuments.map((value) => value.id)),
      )
      .pipe(
        tap(() => {
          const summary =
            this.selectedDocuments?.length === 1
              ? 'Poprawnie przywrócono dokument z archiwum'
              : `Poprawnie przywrócono dokumenty z archiwum`;

          this.#httpResponseService.showSuccessMessage(summary);
        }),
        catchError((err) => {
          const summary =
            this.selectedDocuments?.length === 1
              ? 'Wystąpił błąd podczas próby przywrócenia dokumentu z archiwum'
              : 'Wystąpił błąd podczas próby przywrócenia dokumentów z archiwum';
          this.#httpResponseService.showErrorMessage(err, summary);
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.inboxType.set(InboxType.TO_RECEIVE);
          this.selectedDocuments = [];
          this.#spinnerService.hide();
          this.reloadData();
        }),
      )
      .subscribe();
  }

  #handleRedirectMessageIfExist(): void {
    if (this.redirectMessage() != null) {
      this.#dialogService.open(InfoDialog, {
        closable: true,
        showHeader: false,
        closeOnEscape: true,
        modal: true,
        dismissableMask: true,
        width: '50vw',
        inputValues: {
          ['header']: RedirectMessageContents[this.redirectMessage()!]?.title,
          ['content']:
            RedirectMessageContents[this.redirectMessage()!]?.description,
          ['hideCancelButton']: true,
        },
        breakpoints: {
          '960px': '90vw',
          '640px': '90vw',
        },
      });
      // usuniecie parametru
      const url = new URL(window.location.href);
      url.searchParams.delete('redirectMessage');
      window.history.replaceState({}, document.title, url.href);
    }
  }

  #initInboxTypeChangeEffect(): void {
    effect(() => {
      const type = this.inboxType();
      if (this.#shouldRunInboxTypeChangEffect) {
        this.resetFilters();
        if (type === InboxType.TO_RECEIVE) {
          this.onPrimeFilterChange(true, 'wyslane');
        } else if (type === InboxType.ARCHIVED) {
          this.onPrimeFilterChange(true, 'archiwalne');
        }
      } else {
        this.#shouldRunInboxTypeChangEffect = true;
      }
    });
  }

  clearFilters(): void {
    this.tableFilters = {
      documentType: null,
      sender: null,
      documentNumber: null,
      sentDate: null,
    };
    this.resetFilters();
  }

  showMobilePopoverIfZus(event: Event, document: any): void {
    if (document?.adresat === 'ZUS') {
      this.popRef.toggle(event);
    }
  }
}
